import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { getCurrentInstance } from 'vue'
import { useRouteAccess } from './useRouteAccess'
import { useConnection } from './useConnection'

export interface SubscriptionState {
  interval: NodeJS.Timeout | null
}

export function useSubscription() {
  const store = useStore()
  const route = useRoute()
  const instance = getCurrentInstance()
  const routeAccess = useRouteAccess()
  const connection = useConnection()

  // State
  const state = ref<SubscriptionState>({
    interval: null,
  })

  // Computed properties
  const isConnected = computed(() => store.state.connection.wsConnected)
  const isLoaded = computed(() => store.state.connection.wsLoaded)
  const user = computed(() => store.state.auth.user)
  const pageHidden = computed(() => store.state.pageState.pageHidden)
  
  // Добавляем отдельный computed для idUser для лучшего отслеживания
  const idUser = computed(() => user.value.idUser);

  // WebSocket methods
  const wsSend = (action: string, params?: any) => {
    if (instance?.proxy) {
      (instance.proxy as any).$wsSend(action, params)
    }
  }

  const wsResubscribe = () => {
    if (isLoaded.value) {
      return
    }
    
    if (instance?.proxy) {
      (instance.proxy as any).$wsResubscribe()
    }
  }

  const wsUnsubscribe = (keepLastSubscribe = true) => {
    if (instance?.proxy) {
      (instance.proxy as any).$wsUnsubscribe(keepLastSubscribe)
    }
  }

  // Validation logic
  const validateSubscribe = (): boolean => {
    if (!isConnected.value) {
      return false
    }

    // Check if user is not authenticated and not on guest route and a page is loaded
    if (
      (user.value.idUser === 0) ||
      (user.value.idUser === null) ||
      (user.value.confirmEmail === false)
    ) {
      if (!routeAccess.isGuestRoute.value && isLoaded.value) {
        store.dispatch('connection/wsSetLoaded', false)
        console.log('Set loaded false: guest')
        wsUnsubscribe(true)
        return false
      }
    }

    // Check admin access
    if (routeAccess.isAdminPanel.value && !user.value.admin && isLoaded.value) {
      store.dispatch('connection/wsSetLoaded', false)
      console.log('Set loaded false: admin')
      wsUnsubscribe(true)
      return false
    }

    return true
  }

  const resubscribePage = () => {
    const validationResult = validateSubscribe()
    
    if (validationResult) {
      wsResubscribe()
    }
  }

  // Event handlers
  const eventVisibilitychange = () => {
    if (document.visibilityState === 'visible') {
      if (isLoaded.value === false) {
        console.log('Going to resubscribe now')
        wsResubscribe()
      }
      store.dispatch('pageState/setPageHidden', false)
    } else {
      store.dispatch('pageState/setPageHidden', true)
    }
  }

  const eventFocus = () => {
    store.dispatch('pageState/setPageFocus', true)
  }

  const eventBlur = () => {
    store.dispatch('pageState/setPageFocus', false)
  }

  // Timer management
  const startTimer = () => {
    state.value.interval = setInterval(() => {
      if (pageHidden.value) {
        if (connection.lastPing.value && new Date().getTime() - connection.lastPing.value.getTime() > connection.timeoutSeconds.value * 2 * 1000) {
          console.log('Page hidden timeout [' + (connection.timeoutSeconds.value * 2) + ' seconds]')
          if (isLoaded.value) {
            console.log('Unsubscribe')
            store.dispatch('connection/wsSetLoaded', false)
            console.log('Set loaded false: page hidden timeout [' + (connection.timeoutSeconds.value * 2) + ' seconds]')
            wsUnsubscribe(true)
          }
        }
      }
    }, 251)
  }

  const stopTimer = () => {
    if (state.value.interval) {
      clearInterval(state.value.interval)
      state.value.interval = null
    }
  }

  // Watchers
  const setupWatchers = () => {
    // Watch for user ID changes
    watch(idUser, (to, from) => {
      if ((to !== 0) && (to !== null)) {
        if (validateSubscribe()) {
          // User is authenticated, let the page handle its own subscription
          // No need to track subscribed state - isLoaded handles this
          wsResubscribe()
        } else {
        }
      }
    }, { immediate: true })

    // Watch for route changes - only unsubscribe if we're actually subscribed
    watch(() => route.path, (to) => {
      if (isLoaded.value) {
        wsUnsubscribe(false)
      }
    })
  }

  // Lifecycle methods
  const onMounted = () => {
    window.addEventListener('visibilitychange', eventVisibilitychange)
    window.addEventListener('focus', eventFocus)
    window.addEventListener('blur', eventBlur)
    startTimer()
    setupWatchers()
  }

  const onUnmounted = () => {
    window.removeEventListener('visibilitychange', eventVisibilitychange)
    window.removeEventListener('focus', eventFocus)
    window.removeEventListener('blur', eventBlur)
    stopTimer()
  }

  return {
    // Computed
    isConnected,
    isLoaded,
    user,
    pageHidden,
    routeAccess,
    
    // Methods
    validateSubscribe,
    resubscribePage,
    eventVisibilitychange,
    eventFocus,
    eventBlur,
    startTimer,
    stopTimer,
    onMounted,
    onUnmounted,
  }
} 